/**
 * Feel AWSM chat widget — self-contained, vanilla JS, Shadow DOM.
 * Step 4/6: front-end face + wiring to Worker /api/chat. No lead capture yet (step 5).
 *
 * Drop-in: <script src=".../chat-widget.js" defer></script>
 * Worker URL is set in WORKER_URL (local dev default; prod set at step 6).
 */
(function () {
  "use strict";
  if (window.__feelChatLoaded) return;
  window.__feelChatLoaded = true;

  // ── Config ──────────────────────────────────────────────────────────────
  // ── Worker endpoint ───────────────────────────────────────────────────────
  // Single source of truth. To move to a custom domain later, change ONLY this:
  //   e.g. "https://chat.feelawsm.com"   (no trailing slash, no path)
  // Local dev:  "http://127.0.0.1:8787"
  const WORKER_BASE = "https://feelawsm-chatbot-production.david-fundus.workers.dev";
  const WORKER_URL = WORKER_BASE + "/api/chat";
  const LEAD_URL = WORKER_BASE + "/api/lead";
  const MAX_HISTORY = 20; // 10 exchanges
  const MAX_MESSAGE_LEN = 1500; // mirrors server cap (anti prompt-stuffing)

  // ── Locale ──────────────────────────────────────────────────────────────
  function detectLang() {
    const p = (window.location.pathname || "").toLowerCase();
    if (p.startsWith("/sl-si") || p.startsWith("/sl")) return "sl";
    if (p.startsWith("/de-de") || p.startsWith("/de")) return "de";
    const htmlLang = (document.documentElement.lang || "").toLowerCase();
    if (htmlLang.startsWith("sl")) return "sl";
    if (htmlLang.startsWith("de")) return "de";
    return "en";
  }
  const LANG = detectLang();

  const I18N = {
    sl: {
      bubble: "Imate vprašanje? Tukaj sem.",
      title: "Feel AWSM pomočnica",
      assistant: "Asistentka",
      subtitle: "Običajno odgovori v nekaj sekundah",
      welcome: "Pozdravljeni! 🌸 Kako vam lahko pomagam — izbira izdelka, sestavine ali način uporabe?",
      quick: ["Priporoči mi izdelek", "Dostava", "Stik z ekipo"],
      placeholder: "Vprašajte karkoli…",
      send: "Pošlji",
      close: "Zapri",
      open: "Odpri klepet",
      error: "Oprostite, prišlo je do napake. Poskusite znova.",
      rateLimit: "Ojoj, malo prehitro 🌸 Prejela sem preveč zahtev v kratkem času. Prosim počakajte minuto in poskusite znova.",
      tooLong: "Vaše sporočilo je predolgo (največ 1500 znakov). Prosim, malo ga skrajšajte. 🌸",
    },
    en: {
      bubble: "Have a question? I'm here.",
      title: "Feel AWSM Assistant",
      assistant: "Assistant",
      subtitle: "Usually replies in seconds",
      welcome: "Hi! 🌸 How can I help — product choice, ingredients or how to use?",
      quick: ["Recommend a product", "Shipping", "Talk to the team"],
      placeholder: "Ask anything…",
      send: "Send",
      close: "Close",
      open: "Open chat",
      error: "Sorry, something went wrong. Please try again.",
      rateLimit: "Whoa, a bit fast 🌸 I've had too many requests in a short time. Please wait a minute and try again.",
      tooLong: "Your message is a little too long (max 1500 characters). Could you shorten it? 🌸",
    },
    de: {
      bubble: "Eine Frage? Ich bin da.",
      title: "Feel AWSM Assistentin",
      assistant: "Assistentin",
      subtitle: "Antwortet meist in Sekunden",
      welcome: "Hallo! 🌸 Wie kann ich helfen — Produktauswahl, Inhaltsstoffe oder Anwendung?",
      quick: ["Produkt empfehlen", "Versand", "Team kontaktieren"],
      placeholder: "Frag mich alles…",
      send: "Senden",
      close: "Schließen",
      open: "Chat öffnen",
      error: "Entschuldigung, etwas ist schiefgelaufen. Bitte versuche es erneut.",
      rateLimit: "Oje, etwas schnell 🌸 Ich habe zu viele Anfragen in kurzer Zeit erhalten. Bitte warte eine Minute und versuche es erneut.",
      tooLong: "Deine Nachricht ist etwas zu lang (max. 1500 Zeichen). Bitte kürze sie etwas. 🌸",
    },
  };
  // Lead form strings (the "Talk to the team" quick button opens this form).
  I18N.sl.lead = {
    title: "Stik z ekipo", sub: "Odgovorimo v 24 urah",
    name: "Vaše ime", email: "E-pošta", message: "Vaše sporočilo",
    submit: "Pošlji sporočilo", sending: "Pošiljanje…", back: "Nazaj",
    success: "Hvala! 🌸 Ekipa vam odgovori kmalu.",
    err_required: "Prosimo, izpolnite vsa polja.",
    err_email: "Vnesite veljaven e-poštni naslov.",
    err_send: "Pošiljanje ni uspelo. Poskusite znova.",
  };
  I18N.en.lead = {
    title: "Talk to the team", sub: "We reply within 24 hours",
    name: "Your name", email: "Email", message: "Your message",
    submit: "Send message", sending: "Sending…", back: "Back",
    success: "Thanks! 🌸 The team will get back to you soon.",
    err_required: "Please fill in all fields.",
    err_email: "Please enter a valid email address.",
    err_send: "Couldn't send. Please try again.",
  };
  I18N.de.lead = {
    title: "Team kontaktieren", sub: "Wir antworten innerhalb von 24 Stunden",
    name: "Dein Name", email: "E-Mail", message: "Deine Nachricht",
    submit: "Nachricht senden", sending: "Senden…", back: "Zurück",
    success: "Danke! 🌸 Das Team meldet sich bald bei dir.",
    err_required: "Bitte fülle alle Felder aus.",
    err_email: "Bitte gib eine gültige E-Mail-Adresse ein.",
    err_send: "Senden fehlgeschlagen. Bitte versuche es erneut.",
  };
  const T = I18N[LANG] || I18N.en;
  const L = T.lead;

  // ── Inline SVGs (no icon library) ─────────────────────────────────────────
  const SVG_CHAT =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="26" height="26"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>';
  const SVG_CLOSE =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" width="20" height="20"><path d="M18 6 6 18M6 6l12 12"/></svg>';
  const SVG_SEND =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
  const SVG_SPARK =
    '<svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2z"/></svg>';
  const SVG_MAIL =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="19" height="19"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>';
  const SVG_BACK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15"><path d="M15 18l-6-6 6-6"/></svg>';
  // Feel AWSM brand mark (theme assets/feel-awsm-logo.svg) — monochrome currentColor,
  // recolored white on the rose header via CSS. Inlined so the widget is self-contained
  // (no CDN / local-vs-prod URL); sized via .head-logo CSS.
  const SVG_LOGO =
    '<svg viewBox="0 0 252 252" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M86.57,28.29V11.62h14.16v2.91H89.48v13.75H86.57z M91.59,21.39v-2.54h8.43v2.54H91.59z"/>' +
    '<path d="M107.31,28.29V11.62h14.9v2.91h-11.99v10.84h12.01v2.91H107.31z M112.33,21.04v-2.45h9.24v2.45H112.33z"/>' +
    '<path d="M129.29,28.29V11.62h14.9v2.91H132.2v10.84h12.01v2.91H129.29z M134.31,21.04v-2.45h9.24v2.45H134.31z"/>' +
    '<path d="M151.27,28.29V11.62h2.91v13.75h11.21v2.91H151.27z"/>' +
    '<path d="M50.65,119.21L74.8,57.97h9.66l24.06,61.24H95.93l-5.61-15.27H68.85l-5.61,15.27H50.65z M79.63,70.82l-8.8,24.76h17.34L79.63,70.82z"/>' +
    '<path d="M143.32,58.14h10.95l6.9,20.27l6.9-20.27h11.04L168.68,86l7.76,19.58l17.34-47.61h12.94l-24.93,61.24h-10l-10.61-25.7l-10.61,25.7h-10l-24.84-61.24h12.85l17.42,47.61L153.59,86L143.32,58.14z"/>' +
    '<path d="M96.25,148.97c-0.4-0.4-1.12-0.94-2.17-1.62c-1.05-0.68-2.33-1.35-3.83-2c-1.5-0.65-3.14-1.21-4.9-1.66c-1.76-0.46-3.55-0.68-5.37-0.68c-3.24,0-5.65,0.6-7.24,1.79c-1.59,1.19-2.39,2.87-2.39,5.02c0,1.25,0.3,2.29,0.89,3.11c0.6,0.82,1.46,1.55,2.6,2.17c1.14,0.63,2.57,1.19,4.3,1.71c1.73,0.51,3.73,1.05,6.01,1.62c2.95,0.8,5.63,1.65,8.05,2.56c2.41,0.91,4.46,2.04,6.13,3.41c1.67,1.36,2.97,2.99,3.88,4.9c0.91,1.9,1.36,4.25,1.36,7.03c0,3.24-0.61,6-1.83,8.3c-1.22,2.3-2.85,4.16-4.9,5.58c-2.04,1.42-4.43,2.46-7.16,3.11c-2.72,0.65-5.56,0.98-8.52,0.98c-4.54,0-9.03-0.68-13.45-2.04s-8.4-3.29-11.92-5.79l5.19-10.14c0.51,0.51,1.44,1.18,2.77,2c1.33,0.82,2.91,1.65,4.73,2.47c1.82,0.82,3.83,1.52,6.05,2.09c2.21,0.57,4.48,0.85,6.81,0.85c6.47,0,9.71-2.07,9.71-6.22c0-1.36-0.37-2.5-1.11-3.41c-0.74-0.91-1.79-1.7-3.15-2.39c-1.36-0.68-3.01-1.31-4.94-1.87c-1.93-0.57-4.09-1.16-6.47-1.79c-2.9-0.79-5.41-1.66-7.54-2.6c-2.13-0.94-3.9-2.03-5.32-3.28c-1.42-1.25-2.48-2.72-3.19-4.43c-0.71-1.7-1.06-3.72-1.06-6.05c0-3.06,0.56-5.79,1.7-8.18c1.14-2.38,2.71-4.37,4.73-5.96c2.01-1.59,4.36-2.8,7.03-3.62s5.54-1.24,8.6-1.24c4.26,0,8.18,0.67,11.75,2c3.58,1.33,6.7,2.91,9.37,4.73L96.25,148.97z"/>' +
    '<path d="M179.06,193.6v-40.02l-15.58,29.81h-6.39l-15.58-29.81v40.02h-11.75v-60.47h12.61l17.88,34.41l18.05-34.41h12.52v60.47H179.06z"/>' +
    '<polygon points="239,240.56 12.52,240.56 12.52,131.42 23.85,120.8 11.96,107.92 11.96,12.27 77.09,12.27 77.09,21.27 20.96,21.27 20.96,104.4 36.53,121.26 21.52,135.32 21.52,231.56 230,231.56 230,135.32 214.99,121.26 230.57,104.4 230.57,21.27 174.44,21.27 174.44,12.27 239.57,12.27 239.57,107.92 227.67,120.8 239,131.42"/>' +
    '</svg>';

  // ── Styles (scoped inside Shadow DOM) ─────────────────────────────────────
  const CSS = `
:host { all: initial; }
*, *::before, *::after { box-sizing: border-box; }
.wrap {
  --rose-700:#c0145c; --rose-500:#e5186e; --rose-100:#fce8ef;
  --ink-900:#14110f; --ink-500:#756b64; --ink-200:#e7dfd8;
  --cream:#fbf6f2; --paper:#fff;
  --serif:"Fraunces", Georgia, serif;
  --sans:"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  position: fixed; right: 20px; bottom: 20px; z-index: 2147483000;
  font-family: var(--sans); color: var(--ink-900);
}
/* Launcher */
.launcher { display:flex; flex-direction:column; align-items:flex-end; gap:10px; }
.bubble {
  background: var(--paper); color: var(--ink-900); font-family: var(--serif);
  font-size: 14px; line-height:1.35; padding: 10px 14px; max-width: 220px;
  border-radius: 16px 16px 4px 16px; box-shadow: 0 8px 24px rgba(20,17,15,.14);
  border: 1px solid var(--ink-200); cursor: pointer;
}
.fab {
  width:56px; height:56px; border-radius:50%; border:none; cursor:pointer;
  background: var(--rose-700); color:#fff; display:flex; align-items:center; justify-content:center;
  box-shadow: 0 10px 28px rgba(192,20,92,.42); transition: background .2s, transform .2s;
  align-self:flex-end;
}
.fab:hover { background: var(--rose-500); transform: translateY(-2px); }
.fab:focus-visible { outline: 3px solid var(--rose-100); outline-offset:2px; }
/* Panel */
.panel {
  width: 360px; height: 520px; max-height: calc(100vh - 40px);
  background: var(--cream); border-radius: 20px; overflow: hidden;
  display: none; flex-direction: column;
  box-shadow: 0 24px 60px rgba(20,17,15,.28); border: 1px solid var(--ink-200);
}
.open .panel { display:flex; }
.open .launcher { display:none; }
.head {
  background: var(--rose-700); color:#fff; padding: 14px 16px;
  display:flex; align-items:center; gap:10px;
}
.head-logo { width:40px; height:40px; flex-shrink:0; display:flex; align-items:center; justify-content:center; color:#fff; }
.head-logo svg { width:100%; height:100%; display:block; }
.head-text { flex:1; min-width:0; }
.head-title { font-family: var(--serif); font-size: 17px; font-weight:500; line-height:1.1; letter-spacing:.01em; }
.head-sub { font-size: 11.5px; opacity:.85; margin-top:2px; }
.head-close, .head-mail { background:none; border:none; color:#fff; cursor:pointer; padding:4px;
  border-radius:8px; display:flex; }
.head-close:hover, .head-mail:hover { background: rgba(255,255,255,.15); }
/* Body */
.body { flex:1; overflow-y:auto; padding:16px; display:flex; flex-direction:column; gap:10px; }
.msg { max-width: 82%; font-size:14px; line-height:1.5; padding:10px 13px; white-space:pre-wrap; word-wrap:break-word; }
.msg.bot { background: var(--paper); border:1px solid var(--ink-200); color:var(--ink-900);
  border-radius:14px 14px 14px 4px; align-self:flex-start; }
.msg.user { background: var(--rose-700); color:#fff; border-radius:14px 14px 4px 14px; align-self:flex-end; }
.msg.bot a { color: var(--rose-700); }
.msg.bot strong { font-weight:600; }
.quick { display:flex; flex-wrap:wrap; gap:8px; margin-top:2px; }
.quick button {
  background: var(--rose-100); color: var(--rose-700); border:none; cursor:pointer;
  font-family: var(--sans); font-size:12.5px; font-weight:600; padding:8px 12px; border-radius:999px;
  transition: background .15s;
}
.quick button:hover { background:#f9d2e0; }
.typing { align-self:flex-start; background:var(--paper); border:1px solid var(--ink-200);
  border-radius:14px 14px 14px 4px; padding:12px 14px; display:none; }
.typing.show { display:block; }
.typing span { display:inline-block; width:7px; height:7px; margin:0 2px; border-radius:50%;
  background: var(--ink-200); animation: tdot 1.2s infinite; }
.typing span:nth-child(2){ animation-delay:.2s; } .typing span:nth-child(3){ animation-delay:.4s; }
@keyframes tdot { 0%,60%,100%{ transform:translateY(0); opacity:.5;} 30%{ transform:translateY(-5px); opacity:1;} }
/* Footer */
.foot { padding:12px; background:var(--paper); border-top:1px solid var(--ink-200);
  display:flex; align-items:flex-end; gap:8px; }
.foot textarea {
  flex:1; resize:none; border:1px solid var(--ink-200); border-radius:14px; padding:10px 12px;
  font-family:var(--sans); font-size:14px; color:var(--ink-900); max-height:96px; line-height:1.4;
  background:var(--paper);
}
.foot textarea:focus { outline:none; border-color: var(--rose-500); }
.foot textarea::placeholder { color: var(--ink-500); }
.send { width:40px; height:40px; flex-shrink:0; border:none; border-radius:50%; cursor:pointer;
  background: var(--rose-700); color:#fff; display:flex; align-items:center; justify-content:center;
  transition: background .15s; }
.send:hover { background: var(--rose-500); }
.send:disabled { opacity:.45; cursor:default; }
/* Lead form screen */
.lead { display:none; flex:1; flex-direction:column; overflow-y:auto; padding:18px 16px; gap:12px; }
.lead-open .body, .lead-open .foot { display:none; }
.lead-open .lead { display:flex; }
.lead-back { align-self:flex-start; background:none; border:none; color:var(--rose-700); cursor:pointer;
  font-family:var(--sans); font-size:13px; font-weight:600; display:flex; align-items:center; gap:5px; padding:2px 0; }
.lead-h { font-family:var(--serif); font-size:20px; font-weight:500; color:var(--ink-900); margin:2px 0 0; }
.lead-sub { font-size:13px; color:var(--ink-500); margin-bottom:4px; }
.lead label { font-size:12px; font-weight:600; color:var(--ink-900); display:block; margin-bottom:5px; }
.lead .field { display:flex; flex-direction:column; }
.lead input, .lead textarea {
  width:100%; border:1px solid var(--ink-200); border-radius:12px; padding:10px 12px;
  font-family:var(--sans); font-size:14px; color:var(--ink-900); background:var(--paper); resize:vertical;
}
.lead input:focus, .lead textarea:focus { outline:none; border-color:var(--rose-500); }
.lead textarea { min-height:90px; }
.lead-submit {
  background:var(--rose-700); color:#fff; border:none; border-radius:12px; padding:12px; cursor:pointer;
  font-family:var(--sans); font-size:14px; font-weight:600; transition:background .15s; margin-top:2px;
}
.lead-submit:hover { background:var(--rose-500); }
.lead-submit:disabled { opacity:.5; cursor:default; }
.lead-err { color:#b00020; font-size:12.5px; min-height:16px; }
.lead-ok { text-align:center; padding:30px 12px; font-family:var(--serif); font-size:16px; color:var(--ink-900); line-height:1.5; }
/* Mobile */
@media (max-width: 480px) {
  .wrap { right:0; bottom:0; left:0; }
  /* Fill the viewport exactly via fixed inset — avoids 100vw overshoot
     (scrollbar/vw rounding) that pushed right-aligned msgs + send button
     off-screen. dvh keeps the footer above the iOS keyboard. */
  .panel {
    position: fixed; top:0; left:0; right:0;
    width:auto; border-radius:0;
    height:100vh; height:100dvh;
    max-height:100vh; max-height:100dvh;
  }
  .launcher { position:fixed; right:16px; bottom:16px; }
}
`;

  // ── Build DOM ──────────────────────────────────────────────────────────────
  const host = document.createElement("div");
  host.id = "feel-chat-widget";
  const root = host.attachShadow({ mode: "open" }); // shadow works on detached node
  // Dawn base.css ships `div:empty { display:none }` (specificity 0-1-1, no !important).
  // A shadow host has NO light-DOM children (all markup lives in shadowRoot), so it matches
  // `:empty` → the whole widget gets display:none and renders 0×0. Our `:host{all:initial}`
  // sets display:inline (0-1-0) and loses. An inline style beats the normal stylesheet rule.
  host.style.display = "block";
  // Mount to <body> — guard against being executed before body exists
  // (e.g. injected in <head> without defer when embedded in the theme, step 6).
  if (document.body) {
    document.body.appendChild(host);
  } else {
    document.addEventListener("DOMContentLoaded", () => document.body.appendChild(host));
  }

  const wrap = document.createElement("div");
  wrap.className = "wrap";
  wrap.innerHTML = `
<style>${CSS}</style>
<div class="launcher">
  <button class="fab" data-open aria-label="${T.open}">${SVG_CHAT}</button>
</div>
<div class="panel" role="dialog" aria-modal="true" aria-label="${T.title}">
  <div class="head">
    <div class="head-logo" aria-hidden="true">${SVG_LOGO}</div>
    <div class="head-text">
      <div class="head-title">${T.assistant}</div>
    </div>
    <button class="head-mail" data-lead-open aria-label="${L.title}" title="${L.title}">${SVG_MAIL}</button>
    <button class="head-close" data-close aria-label="${T.close}">${SVG_CLOSE}</button>
  </div>
  <div class="body" data-body>
    <div class="msg bot">${T.welcome}</div>
    <div class="quick" data-quick>
      ${T.quick.map((q) => `<button type="button">${q}</button>`).join("")}
    </div>
    <div class="typing" data-typing aria-hidden="true"><span></span><span></span><span></span></div>
  </div>
  <div class="foot">
    <textarea data-input rows="1" placeholder="${T.placeholder}" aria-label="${T.placeholder}"></textarea>
    <button class="send" data-send aria-label="${T.send}">${SVG_SEND}</button>
  </div>
  <div class="lead" data-lead>
    <button class="lead-back" data-lead-back type="button">${SVG_BACK} ${L.back}</button>
    <h3 class="lead-h">${L.title}</h3>
    <div class="lead-sub">${L.sub}</div>
    <div class="lead-form" data-lead-form>
      <div class="field"><label>${L.name}</label><input data-lead-name type="text" maxlength="100" autocomplete="name"></div>
      <div class="field"><label>${L.email}</label><input data-lead-email type="email" maxlength="200" autocomplete="email"></div>
      <div class="field"><label>${L.message}</label><textarea data-lead-message maxlength="2000"></textarea></div>
      <div class="lead-err" data-lead-err aria-live="polite"></div>
      <button class="lead-submit" data-lead-submit type="button">${L.submit}</button>
    </div>
  </div>
</div>`;
  root.appendChild(wrap);

  // ── Refs ─────────────────────────────────────────────────────────────────
  const $ = (s) => root.querySelector(s);
  const bodyEl = $("[data-body]");
  const typingEl = $("[data-typing]");
  const inputEl = $("[data-input]");
  const sendBtn = $("[data-send]");
  const quickEl = $("[data-quick]");

  let history = [];
  let busy = false;

  // ── Helpers ──────────────────────────────────────────────────────────────
  function esc(s) {
    return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  }
  /* The chat backend (Cloudflare Worker) has no notion of the shopper's current
     market/locale — it always returns plain root-relative-origin URLs, e.g.
     https://feelawsm.com/blogs/journal/x. Shopify Markets subfolders (/sl-si/,
     /de-de/) are NOT applied to such raw strings automatically (same class of
     bug as the theme's feel-loc-url fix). Rewrite same-origin links to carry
     the CURRENT page's locale prefix before they're ever turned into <a href>. */
  function localizeUrl(rawUrl) {
    try {
      var u = new URL(rawUrl, window.location.origin);
      if (u.origin !== window.location.origin) return rawUrl; // external (e.g. PubMed) — leave alone
      if (/^\/(sl-si|de-de)(\/|$)/i.test(u.pathname)) return rawUrl; // already locale-prefixed
      var prefix = LANG === "sl" ? "/sl-si" : LANG === "de" ? "/de-de" : "";
      u.pathname = prefix + u.pathname;
      return u.toString();
    } catch (e) {
      return rawUrl;
    }
  }
  /* Render bot text: escape, then linkify [text](url) + bare urls, **bold**, newlines. */
  function renderBot(text) {
    let h = esc(text);
    h = h.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, function (m, label, url) {
      return '<a href="' + localizeUrl(url) + '" target="_blank" rel="noopener">' + label + "</a>";
    });
    h = h.replace(/(^|[\s(])(https?:\/\/[^\s)]+)/g, function (m, pre, url) {
      return pre + '<a href="' + localizeUrl(url) + '" target="_blank" rel="noopener">' + url + "</a>";
    });
    h = h.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    return h;
  }
  function addMsg(text, who) {
    const d = document.createElement("div");
    d.className = "msg " + who;
    d.innerHTML = who === "bot" ? renderBot(text) : esc(text);
    bodyEl.insertBefore(d, typingEl);
    bodyEl.scrollTop = bodyEl.scrollHeight;
    return d;
  }
  function showTyping(on) {
    typingEl.classList.toggle("show", on);
    if (on) bodyEl.scrollTop = bodyEl.scrollHeight;
  }
  function setBusy(b) {
    busy = b;
    sendBtn.disabled = b;
  }

  async function send(text) {
    text = (text || "").trim();
    if (!text || busy) return;
    if (quickEl) quickEl.style.display = "none";
    addMsg(text, "user");
    inputEl.value = "";
    autosize();
    // Client-side length guard (mirrors server cap) — answer locally, no request.
    if (text.length > MAX_MESSAGE_LEN) {
      addMsg(T.tooLong, "bot");
      return;
    }
    setBusy(true);
    showTyping(true);
    try {
      const res = await fetch(WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, history: history.slice(-MAX_HISTORY), lang: LANG }),
      });
      const data = await res.json().catch(() => ({}));
      showTyping(false);
      if (res.status === 429) {
        // Rate limited — show as a calm bot message, not an error.
        addMsg(data.error || T.rateLimit, "bot");
      } else if (res.status === 400 && data && data.tooLong) {
        addMsg(data.error || T.tooLong, "bot");
      } else if (!res.ok || !data.reply) {
        addMsg(T.error, "bot");
      } else {
        addMsg(data.reply, "bot");
        history.push({ role: "user", content: text }, { role: "assistant", content: data.reply });
        if (history.length > MAX_HISTORY) history = history.slice(-MAX_HISTORY);
      }
    } catch (e) {
      showTyping(false);
      addMsg(T.error, "bot");
    } finally {
      setBusy(false);
      inputEl.focus();
    }
  }

  // ── Mobile: pin the open panel to the VISUAL viewport ──────────────────────
  // Fixes (a) the send button clipping off-screen when the document is wider than
  // the visible area (iOS resolves position:fixed against the layout viewport, not
  // the visual one) and (b) the keyboard covering the input while typing — the
  // visual viewport shrinks above the keyboard, so we re-pin the panel onto it.
  const panelEl = $(".panel");
  const mqMobile = window.matchMedia("(max-width: 480px)");
  function syncPanelViewport() {
    const p = panelEl.style;
    if (!mqMobile.matches || !wrap.classList.contains("open")) {
      p.position = p.left = p.top = p.width = p.height = p.maxHeight = "";
      return;
    }
    const vv = window.visualViewport;
    p.position = "fixed";
    p.left = Math.round(vv ? vv.offsetLeft : 0) + "px";
    p.top = Math.round(vv ? vv.offsetTop : 0) + "px";
    p.width = Math.round(vv ? vv.width : window.innerWidth) + "px";
    p.height = Math.round(vv ? vv.height : window.innerHeight) + "px";
    p.maxHeight = p.height;
  }
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", syncPanelViewport);
    window.visualViewport.addEventListener("scroll", syncPanelViewport);
  }
  window.addEventListener("resize", syncPanelViewport);
  window.addEventListener("orientationchange", () => setTimeout(syncPanelViewport, 250));

  // ── Open / close + focus management ────────────────────────────────────────
  function open() {
    wrap.classList.add("open");
    syncPanelViewport();
    setTimeout(() => { inputEl.focus(); syncPanelViewport(); }, 50);
  }
  function close() {
    wrap.classList.remove("open");
    syncPanelViewport();
    root.querySelector(".fab").focus();
  }

  root.querySelectorAll("[data-open]").forEach((el) => {
    el.addEventListener("click", open);
    el.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
    });
  });
  $("[data-close]").addEventListener("click", close);

  // Quick buttons: index 2 ("Talk to the team") opens the lead form; others chat.
  quickEl.querySelectorAll("button").forEach((b, i) =>
    b.addEventListener("click", () => (i === 2 ? openLead() : send(b.textContent)))
  );

  sendBtn.addEventListener("click", () => send(inputEl.value));

  // ── Lead form ──────────────────────────────────────────────────────────
  const leadName = $("[data-lead-name]");
  const leadEmail = $("[data-lead-email]");
  const leadMsg = $("[data-lead-message]");
  const leadErr = $("[data-lead-err]");
  const leadSubmit = $("[data-lead-submit]");
  const leadForm = $("[data-lead-form]");
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function openLead() {
    wrap.classList.add("lead-open");
    leadErr.textContent = "";
    setTimeout(() => leadName.focus(), 50);
  }
  function closeLead() {
    wrap.classList.remove("lead-open");
  }
  $("[data-lead-back]").addEventListener("click", closeLead);
  root.querySelectorAll("[data-lead-open]").forEach((el) => el.addEventListener("click", openLead));

  async function submitLead() {
    const name = leadName.value.trim();
    const email = leadEmail.value.trim();
    const message = leadMsg.value.trim();
    if (!name || !email || !message) { leadErr.textContent = L.err_required; return; }
    if (!EMAIL_RE.test(email)) { leadErr.textContent = L.err_email; return; }
    leadErr.textContent = "";
    leadSubmit.disabled = true;
    const orig = leadSubmit.textContent;
    leadSubmit.textContent = L.sending;
    try {
      const res = await fetch(LEAD_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, lang: LANG }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        leadForm.innerHTML = '<div class="lead-ok">' + L.success + "</div>";
      } else {
        leadErr.textContent = L.err_send;
        leadSubmit.disabled = false;
        leadSubmit.textContent = orig;
      }
    } catch (e) {
      leadErr.textContent = L.err_send;
      leadSubmit.disabled = false;
      leadSubmit.textContent = orig;
    }
  }
  leadSubmit.addEventListener("click", submitLead);

  function autosize() {
    inputEl.style.height = "auto";
    inputEl.style.height = Math.min(inputEl.scrollHeight, 96) + "px";
  }
  inputEl.addEventListener("input", autosize);
  inputEl.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(inputEl.value); }
  });

  // Esc closes + basic focus trap inside panel
  wrap.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && wrap.classList.contains("open")) {
      if (wrap.classList.contains("lead-open")) { closeLead(); } else { close(); }
      return;
    }
    if (e.key !== "Tab" || !wrap.classList.contains("open")) return;
    const f = [...root.querySelectorAll(".panel button, .panel textarea, .panel input, .panel a")].filter(
      (el) => !el.disabled && el.offsetParent !== null
    );
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && root.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && root.activeElement === last) { e.preventDefault(); first.focus(); }
  });
})();
